import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { NotificationBell } from './NotificationBell';
import { HelpPanel } from './HelpPanel';
import { 
  Home, FileText, Users, BarChart3, Shield, Settings,
  Menu, X, LogOut, ChevronRight, Plus, Building2,
  BookOpen, PieChart, CalendarDays, Contact, TrendingUp, User, Zap
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/', label: 'Today', icon: Home, description: 'Daily tasks' },
  { path: '/dashboard', label: 'Dashboard', icon: BarChart3, description: 'Overview' },
  { path: '/enquiries', label: 'Enquiries', icon: FileText, description: 'Projects' },
  { path: '/contacts', label: 'Contacts', icon: Contact, description: 'People' },
  { path: '/pipeline', label: 'Pipeline', icon: Users, description: 'Leads' },
  { path: '/sequences', label: 'Sequences', icon: Zap, description: 'Follow-ups' },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays, description: 'Schedule' },
  { path: '/analytics', label: 'Analytics', icon: TrendingUp, description: 'Insights' },
  { path: '/reports', label: 'Reports', icon: PieChart, description: 'Weekly' },
  { path: '/library', label: 'Library', icon: BookOpen, description: 'Scripts' },
  { path: '/approvals', label: 'Approvals', icon: Shield, description: 'Reviews' },
];

const MOBILE_NAV_ITEMS = [
  { path: '/', label: 'Today', icon: Home },
  { path: '/enquiries', label: 'Enquiries', icon: FileText },
  { path: '/contacts', label: 'Contacts', icon: Contact },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
  { path: '/dashboard', label: 'More', icon: Menu },
];

// All pages available for mobile nav customisation
const ALL_MOBILE_PAGES = [
  { path: '/', label: 'Today', icon: Home },
  { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
  { path: '/enquiries', label: 'Enquiries', icon: FileText },
  { path: '/contacts', label: 'Contacts', icon: Contact },
  { path: '/pipeline', label: 'Pipeline', icon: Users },
  { path: '/sequences', label: 'Sequences', icon: Zap },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
  { path: '/analytics', label: 'Analytics', icon: TrendingUp },
  { path: '/reports', label: 'Reports', icon: PieChart },
  { path: '/library', label: 'Library', icon: BookOpen },
  { path: '/approvals', label: 'Approvals', icon: Shield },
];

const DEFAULT_MOBILE_NAV_PATHS = ['/', '/enquiries', '/pipeline', '/calendar'];
const MOBILE_PAGE_MAP = Object.fromEntries(ALL_MOBILE_PAGES.map(p => [p.path, p]));

// Helper function to lighten/darken colors for gradients
const adjustColor = (hex, percent) => {
  if (!hex) return '#243f66';
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

export const MainLayout = ({ children }) => {
  const { user, logout, authFetch } = useAuth();
  const { branding, getLogoUrl } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
 
  const [collapsed, _setCollapsed] = useState(false);
  const [envInfo, setEnvInfo] = useState(null);
  const [mobileNavPaths, setMobileNavPaths] = useState(
    () => {
      try {
        const stored = localStorage.getItem('opsflow_mobile_nav');
        return stored ? JSON.parse(stored) : DEFAULT_MOBILE_NAV_PATHS;
      } catch { return DEFAULT_MOBILE_NAV_PATHS; }
    }
  );

  // Fetch environment info once on mount (non-blocking)
  useEffect(() => {
    const fetchEnvInfo = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/health`);
        if (res.ok) {
          const data = await res.json();
          setEnvInfo({
            environment: data.environment || 'unknown',
            db_name: data.db_name || 'unknown',
            db_type: data.db_type || 'unknown'
          });
        }
      } catch {
        // Silent fail - environment indicator is non-critical
      }
    };
    fetchEnvInfo();
  }, []);

  // Fetch user's mobile nav preferences
  useEffect(() => {
    if (!user) return;
    const fetchNavPrefs = async () => {
      try {
        const res = await authFetch('/me/mobile-nav');
        if (res.ok) {
          const data = await res.json();
          if (data.items?.length) {
            setMobileNavPaths(data.items);
            localStorage.setItem('opsflow_mobile_nav', JSON.stringify(data.items));
          }
        }
      } catch { /* silent */ }
    };
    fetchNavPrefs();
    // Listen for updates from MyProfilePage
    const onNavUpdate = (e) => setMobileNavPaths(e.detail);
    window.addEventListener('mobile-nav-updated', onNavUpdate);
    return () => window.removeEventListener('mobile-nav-updated', onNavUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isDirector = user?.role === 'director';

  // Get logo URL from branding context
  const logoUrl = getLogoUrl();

  // Get user initials
  const getInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  // Get role display name
  const getRoleDisplay = (role) => {
    const roleMap = {
      'director': 'Director',
      'bd_manager': 'BD Manager',
      'senior_engineer': 'Senior Eng.',
      'associate_engineer': 'Associate Eng.',
      'engineer': 'Engineer'
    };
    return roleMap[role] || role;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex">
      {/* Desktop Sidebar - Premium Design */}
      <aside 
        className={`hidden md:flex flex-col text-white fixed left-0 top-0 bottom-0 z-40 transition-all duration-300 ${collapsed ? 'w-20' : 'w-72'}`}
        style={{ 
          background: `linear-gradient(to bottom, ${branding?.primary_color || '#0f2744'}, ${branding?.secondary_color || '#1a3a5c'}, ${branding?.primary_color || '#0f2744'})`
        }}
      >
        
        {/* Logo Section - Uses uploaded logo from branding */}
        <div className="p-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            {/* Dynamic Logo from Branding */}
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={branding?.company_name || 'Company Logo'} 
                className="h-10 w-auto max-w-[120px] object-contain"
              />
            ) : (
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            {!collapsed && (
              <span className="font-heading text-lg font-bold text-white truncate max-w-[140px]">
                {branding?.company_name || 'OpsFlow'}
              </span>
            )}
          </div>
        </div>
        
        {/* Quick Actions */}
        {!collapsed && (
          <div className="px-4 py-4">
            <Button
              onClick={() => navigate('/enquiry/new')}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl h-11 font-semibold shadow-lg shadow-emerald-500/20 transition-all hover:shadow-emerald-500/30 hover:scale-[1.02]"
              data-testid="sidebar-new-enquiry"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Enquiry
            </Button>
          </div>
        )}
        
        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          <div className="space-y-1">
            {NAV_ITEMS.map((item, index) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 relative overflow-hidden ${
                    active
                      ? 'bg-white/15 text-white shadow-lg'
                      : 'text-white/60 hover:bg-white/8 hover:text-white'
                  }`}
                  data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Active indicator */}
                  {active && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-r-full" />
                  )}
                  
                  <div className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all ${
                    active 
                      ? 'bg-white/10' 
                      : 'bg-transparent group-hover:bg-white/5'
                  }`}>
                    <Icon className={`w-[18px] h-[18px] transition-transform ${active ? 'scale-110' : 'group-hover:scale-105'}`} />
                  </div>
                  
                  {!collapsed && (
                    <div className="flex-1 text-left">
                      <span className="font-medium text-sm block">{item.label}</span>
                    </div>
                  )}
                  
                  {!collapsed && active && (
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Admin Section - Directors Only */}
          {isDirector && (
            <div className="mt-6 pt-4 border-t border-white/5">
              {!collapsed && (
                <p className="text-[10px] text-white/30 uppercase tracking-widest font-semibold px-3 mb-2">
                  Administration
                </p>
              )}
              <button
                onClick={() => navigate('/admin')}
                className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                  isActive('/admin')
                    ? 'bg-amber-500/20 text-amber-300'
                    : 'text-white/60 hover:bg-white/8 hover:text-white'
                }`}
                data-testid="nav-admin"
              >
                <div className={`flex items-center justify-center w-9 h-9 rounded-lg transition-all ${
                  isActive('/admin') 
                    ? 'bg-amber-500/20' 
                    : 'bg-transparent group-hover:bg-white/5'
                }`}>
                  <Settings className="w-[18px] h-[18px]" />
                </div>
                {!collapsed && (
                  <span className="font-medium text-sm">Admin</span>
                )}
              </button>
            </div>
          )}
        </nav>
        
        {/* User Profile Section */}
        <div className="p-3 border-t border-white/5 bg-black/10">
          {/* Notification Bell and Help */}
          <div className="flex items-center justify-between px-2 mb-3">
            <div className="flex items-center gap-1">
              <NotificationBell />
              <HelpPanel />
            </div>
            {!collapsed && (
              <span className="text-[10px] text-white/40">Actions</span>
            )}
          </div>
          
          {/* User Card */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/8 transition-all group">
                {/* Avatar with gradient ring */}
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    {getInitials()}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#1a3a5c]" />
                </div>
                
                {!collapsed && (
                  <>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-semibold truncate text-white">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-[11px] text-white/50 truncate">
                        {getRoleDisplay(user?.role)}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 p-2">
              <div className="px-2 py-2 mb-2 bg-slate-50 rounded-lg">
                <p className="font-medium text-sm text-slate-900">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuItem onClick={() => navigate('/profile')} className="rounded-lg" data-testid="my-profile-link">
                <User className="w-4 h-4 mr-2" />
                My Profile
              </DropdownMenuItem>
              {isDirector && (
                <>
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="rounded-lg">
                    <Settings className="w-4 h-4 mr-2" />
                    Admin Settings
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600 rounded-lg hover:bg-red-50">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Environment Indicator - subtle footer */}
          {envInfo && !collapsed && (
            <div className="mt-3 px-2 py-1.5 rounded-lg bg-white/5">
              <p className="text-[10px] text-white/40 text-center uppercase tracking-wider">
                {envInfo.environment === 'production' ? '🟢 Production' : '🟡 Preview'}
              </p>
            </div>
          )}
          {envInfo && collapsed && (
            <div className="mt-3 flex justify-center">
              <span className="text-[10px]" title={`${envInfo.environment} - ${envInfo.db_name}`}>
                {envInfo.environment === 'production' ? '🟢' : '🟡'}
              </span>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Header */}
      <div 
        className="md:hidden fixed top-0 left-0 right-0 z-40 shadow-lg"
        style={{ 
          background: branding?.primary_color 
            ? `linear-gradient(to right, ${branding.primary_color}, ${adjustColor(branding.primary_color, 15)})`
            : 'linear-gradient(to right, #1e3a5f, #243f66)'
        }}
      >
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            {/* Dynamic Logo from Branding */}
            {logoUrl ? (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1.5">
                <img 
                  src={logoUrl} 
                  alt={branding?.company_name || 'Company Logo'} 
                  className="h-7 w-auto max-w-[80px] object-contain"
                />
              </div>
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/15 backdrop-blur-sm">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-heading text-base font-bold truncate max-w-[140px] text-white">
              {branding?.company_name || 'OpsFlow'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <HelpPanel />
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-xl hover:bg-white/10 transition-colors"
            >
              {sidebarOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`md:hidden fixed top-14 left-0 bottom-0 w-72 bg-white z-50 transform transition-transform duration-300 ease-out shadow-2xl ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Sidebar Header with branding */}
        <div 
          className="px-4 py-4"
          style={{ 
            background: branding?.primary_color 
              ? `linear-gradient(to right, ${branding.primary_color}, ${adjustColor(branding.primary_color, 15)})`
              : 'linear-gradient(to right, #1e3a5f, #243f66)'
          }}
        >
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <div className="bg-white/15 backdrop-blur-sm rounded-lg p-1.5">
                <img 
                  src={logoUrl} 
                  alt={branding?.company_name || 'Company Logo'} 
                  className="h-8 w-auto max-w-[60px] object-contain"
                />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/15">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <p className="text-white font-semibold text-sm">{branding?.company_name || 'OpsFlow'}</p>
              <p className="text-white/60 text-xs">MEP Consultancy OS</p>
            </div>
          </div>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100%-220px)]">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all`}
                style={active ? {
                  backgroundColor: branding?.primary_color || '#1e3a5f',
                  color: 'white',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                } : {
                  color: '#475569'
                }}
              >
                <div 
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={active ? { backgroundColor: 'rgba(255,255,255,0.2)' } : { backgroundColor: '#f1f5f9' }}
                >
                  <Icon className="w-[18px] h-[18px]" />
                </div>
                <span className="font-medium text-sm">{item.label}</span>
              </button>
            );
          })}
          
          {isDirector && (
            <button
              onClick={() => {
                navigate('/admin');
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all mt-4 ${
                isActive('/admin')
                  ? 'bg-amber-500 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isActive('/admin') ? 'bg-white/20' : 'bg-slate-100'}`}>
                <Settings className="w-[18px] h-[18px]" />
              </div>
              <span className="font-medium text-sm">Admin</span>
            </button>
          )}
          
          {/* My Profile - Always visible */}
          <button
            onClick={() => {
              navigate('/profile');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all mt-2 ${
              isActive('/profile')
                ? 'bg-blue-500 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isActive('/profile') ? 'bg-white/20' : 'bg-slate-100'}`}>
              <User className="w-[18px] h-[18px]" />
            </div>
            <span className="font-medium text-sm">My Profile</span>
          </button>
        </nav>
        
        <div className="absolute bottom-16 left-0 right-0 p-4 border-t border-slate-100">
          <Button
            onClick={() => {
              navigate('/enquiry/new');
              setSidebarOpen(false);
            }}
            className="w-full text-white rounded-xl h-12 font-semibold"
            style={{ 
              background: branding?.secondary_color 
                ? `linear-gradient(to right, ${branding.secondary_color}, ${adjustColor(branding.secondary_color, 15)})`
                : 'linear-gradient(to right, #10b981, #14b8a6)'
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            New Enquiry
          </Button>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 bg-slate-50">
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="font-medium text-sm">Log out</span>
          </button>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex items-stretch h-16">
          {mobileNavPaths.map((path) => {
            const item = MOBILE_PAGE_MAP[path];
            if (!item) return null;
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 transition-all"
                style={{ color: active ? (branding?.primary_color || '#1e3a5f') : '#94a3b8' }}
                data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
              >
                <div 
                  className="p-1.5 rounded-xl transition-all"
                  style={active ? { backgroundColor: `${branding?.primary_color || '#1e3a5f'}15` } : {}}
                >
                  <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5px]' : ''}`} />
                </div>
                <span className={`text-[10px] ${active ? 'font-semibold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}

          {/* More menu — contains pages NOT in the user's chosen 4 */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex-1 flex flex-col items-center justify-center gap-0.5 text-slate-400" data-testid="mobile-nav-more">
                <Menu className="w-5 h-5" />
                <span className="text-[10px] font-medium">More</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 mb-2 mr-2">
              {ALL_MOBILE_PAGES.filter(p => !mobileNavPaths.includes(p.path)).map((page) => {
                const PageIcon = page.icon;
                return (
                  <DropdownMenuItem key={page.path} onClick={() => navigate(page.path)}>
                    <PageIcon className="w-4 h-4 mr-2" />
                    {page.label}
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="w-4 h-4 mr-2" />
                My Profile
              </DropdownMenuItem>
              {isDirector && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/admin')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Admin
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'md:ml-20' : 'md:ml-72'} overflow-x-hidden`}>
        {/* Content wrapper with proper scrolling */}
        <div className="pt-14 md:pt-0 pb-20 md:pb-6 min-h-screen">
          <div className="p-4 md:p-6 lg:p-8 max-w-full">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};
